import { useCallback, useEffect, useRef, useState } from "react";

export type ScoutChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
};

function buildStubResponse(userPrompt: string): string {
  const preview = userPrompt.length > 120 ? `${userPrompt.slice(0, 120)}…` : userPrompt;
  return `Received the prompt "${preview}". Thinking and responding…`;
}

function streamAssistantText(
  fullText: string,
  onChunk: (partial: string) => void,
  onDone: () => void
): () => void {
  let index = 0;
  let cancelled = false;
  const step = 2;
  const delayMs = 18;

  const tick = () => {
    if (cancelled) return;
    index = Math.min(fullText.length, index + step);
    onChunk(fullText.slice(0, index));
    if (index < fullText.length) {
      window.setTimeout(tick, delayMs);
    } else {
      onDone();
    }
  };

  tick();
  return () => {
    cancelled = true;
  };
}

export const useScoutChat = () => {
  const [messages, setMessages] = useState<ScoutChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const cancelStreamRef = useRef<(() => void) | null>(null);
  const streamingAssistantIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      cancelStreamRef.current?.();
    };
  }, []);

  const handleSubmit = useCallback(async (text: string) => {
    // If a previous assistant message is mid-stream, cancel it and mark it not streaming.
    cancelStreamRef.current?.();
    if (streamingAssistantIdRef.current) {
      const previousAssistantId = streamingAssistantIdRef.current;
      streamingAssistantIdRef.current = null;
      setMessages((prev) =>
        prev.map((m) => (m.id === previousAssistantId ? { ...m, isStreaming: false } : m))
      );
    }

    const userMessage: ScoutChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    };
    const assistantId = crypto.randomUUID();
    streamingAssistantIdRef.current = assistantId;
    const stub = buildStubResponse(text);

    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: assistantId, role: "assistant", content: "", isStreaming: true },
    ]);
    setIsSending(true);

    const cancelStream = streamAssistantText(
      stub,
      (partial) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: partial, isStreaming: true } : m
          )
        );
      },
      () => {
        setIsSending(false);
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
        );
        if (streamingAssistantIdRef.current === assistantId) {
          streamingAssistantIdRef.current = null;
        }
        cancelStreamRef.current = null;
      }
    );

    cancelStreamRef.current = () => {
      cancelStream();
      setIsSending(false);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
      );
      if (streamingAssistantIdRef.current === assistantId) {
        streamingAssistantIdRef.current = null;
      }
      cancelStreamRef.current = null;
    };
  }, []);

  return {
    messages,
    isSending,
    handleSubmit,
  };
};
